import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState } from "@/app/rootReducer";
import { selectIntacctId } from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { useToast } from "@/components/ui/use-toast";
import { AppDispatch } from "@/app/store";
import {
  fetchMPDashboardDataAction,
  refreshMPDashboardDataAction,
} from "../slice/mpDashboardSlice";
import {
  GetMPDashboardRequest,
  MPDashboardZipCode,
} from "../api/getMPDashboard/types";
import { QualificationStatus } from "../api/exportMPDashboardCsv/types";
import {
  applyMPDashboardFilters,
  MPDashboardFilters,
  parseMPDashboardFilters,
  StatusFilter,
} from "../utils/mpDashboardFilterParams";
import { useDownloadMPDashboardCsv } from "./useDownloadMPDashboardCsv";
import { useMPDashboardChipFilters } from "./useMPDashboardChipFilters";

export type SortField = keyof Pick<
  MPDashboardZipCode,
  | "zipcode"
  | "totalSales"
  | "totalCustomers"
  | "totalHouseholds"
  | "marketPresence"
>;
export type SortDirection = "asc" | "desc";

const DEFAULT_ROWS_VISIBLE = 10;

export function useMPDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCompanyIntacctId = useSelector(selectIntacctId);
  const { toast } = useToast();

  const intacctId =
    searchParams.get("intacctId") || activeCompanyIntacctId || "";

  const filters = useMemo(
    () => parseMPDashboardFilters(searchParams),
    [searchParams],
  );
  const yearsKey = filters.years.join(",");
  const tradesKey = filters.trades.join(",");
  const statusFilter = filters.status;

  const dashboardRequest: GetMPDashboardRequest = useMemo(
    () => ({
      intacctId,
      years: yearsKey ? yearsKey.split(",") : [],
      trades: tradesKey ? tradesKey.split(",") : [],
    }),
    [intacctId, yearsKey, tradesKey],
  );

  const updateFilters = useCallback(
    (changes: Partial<MPDashboardFilters>) => {
      setSearchParams(
        (current) =>
          applyMPDashboardFilters(current, {
            ...parseMPDashboardFilters(current),
            ...changes,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setStatusFilter = useCallback(
    (status: StatusFilter) => updateFilters({ status }),
    [updateFilters],
  );

  const chipFilters = useMPDashboardChipFilters({
    years: dashboardRequest.years,
    trades: dashboardRequest.trades,
    updateFilters,
  });

  const { dashboardData, loading, refreshing, error } = useSelector(
    (state: RootState) => state.mpDashboard,
  );

  const {
    loading: csvLoading,
    downloadCsv,
    error: csvError,
  } = useDownloadMPDashboardCsv();

  const handleExportCsv = useCallback(() => {
    if (!intacctId) return;
    downloadCsv({
      ...dashboardRequest,
      status: statusFilter === "all" ? undefined : statusFilter,
    });
  }, [intacctId, downloadCsv, dashboardRequest, statusFilter]);

  const [expanded, setExpanded] = useState(false);
  const [sortField, setSortField] = useState<SortField>("totalSales");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleRefresh = useCallback(() => {
    if (!intacctId || refreshing) return;

    dispatch(refreshMPDashboardDataAction(dashboardRequest));
  }, [dispatch, intacctId, refreshing, dashboardRequest]);

  useEffect(() => {
    if (!dashboardRequest.intacctId) return;

    const controller = new AbortController();
    dispatch(fetchMPDashboardDataAction(dashboardRequest, controller.signal));

    return () => controller.abort();
  }, [dispatch, dashboardRequest]);

  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error,
      });
    }
  }, [error, toast]);

  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("desc");
      }
    },
    [sortField],
  );

  const items = dashboardData.items;
  const summary = dashboardData.summary;
  const lastSync = dashboardData.lastSync;

  const statusCounts = useMemo(() => {
    const counts = { all: items.length, recommend: 0, review: 0, low: 0 };
    items.forEach((item) => {
      const status = item.qualificationStatus as QualificationStatus;
      if (status in counts) {
        counts[status]++;
      }
    });
    return counts;
  }, [items]);

  const filteredAndSorted = useMemo(() => {
    let result = items;

    if (statusFilter !== "all") {
      result = result.filter(
        (item) => item.qualificationStatus === statusFilter,
      );
    }

    result = [...result].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const multiplier = sortDirection === "asc" ? 1 : -1;
      if (typeof aVal === "string" && typeof bVal === "string") {
        return aVal.localeCompare(bVal) * multiplier;
      }
      return ((aVal as number) - (bVal as number)) * multiplier;
    });

    return result;
  }, [items, statusFilter, sortField, sortDirection]);

  const filteredSummary = useMemo(() => {
    if (statusFilter === "all") return summary;

    const filtered = filteredAndSorted;
    return {
      totalSales: filtered.reduce((sum, item) => sum + item.totalSales, 0),
      totalCustomers: filtered.reduce(
        (sum, item) => sum + item.totalCustomers,
        0,
      ),
      qualifiedZipCount: filtered.filter(
        (item) => item.qualificationStatus === "recommend",
      ).length,
    };
  }, [summary, statusFilter, filteredAndSorted]);

  const visibleRows = expanded
    ? filteredAndSorted
    : filteredAndSorted.slice(0, DEFAULT_ROWS_VISIBLE);

  const canExpand = filteredAndSorted.length > DEFAULT_ROWS_VISIBLE;

  return {
    loading,
    refreshing,
    items: visibleRows,
    totalFilteredCount: filteredAndSorted.length,
    summary: filteredSummary,
    expanded,
    setExpanded,
    canExpand,
    sortField,
    sortDirection,
    handleSort,
    statusFilter,
    setStatusFilter,
    chipFilters,
    statusCounts,
    totalZipCount: items.length,
    lastSync,
    handleRefresh,
    csvLoading,
    csvError,
    handleExportCsv,
  };
}
